/**
 * Background Renderer — orchestrates parallax layers and per-stage scenes.
 *
 * Replaces the old single-file BackgroundRenderer (120 stars + 3 moons)
 * with a 3-layer parallax system and stage-specific visual elements.
 */

import { Container } from 'pixi.js';
import type { Camera } from 'bonkjs';
import { Z } from '../../constants';
import { getActiveTheme, DEFAULT_STAGE_ID } from '../../stages';
import { getBaseThemeId } from '../../stages/registry';
import { ParallaxLayer } from './parallax-layer';
import type { StageScene, ParallaxLayers } from './stage-scene';
import { NeonVoidScene } from './scenes/neon-void';
import { HellfireScene } from './scenes/hellfire';
import { PaperTheaterScene } from './scenes/paper-theater';
import { MossCircuitScene } from './scenes/moss-circuit';
import { IcescapeScene } from './scenes/icescape';

function createScene(stageId: string): StageScene {
  // Resolve to base theme for background scene selection
  const baseTheme = getBaseThemeId(stageId);
  switch (baseTheme) {
    case 'hellfire':
      return new HellfireScene();
    case 'paper_theater':
      return new PaperTheaterScene();
    case 'moss_circuit':
      return new MossCircuitScene();
    case 'the_shelf':
    case 'stalactite':
      return new IcescapeScene();
    default:
      return new NeonVoidScene();
  }
}

export class BackgroundRenderer {
  private worldContainer: Container;
  private skyLayer: ParallaxLayer;
  private deepLayer: ParallaxLayer;
  private midLayer: ParallaxLayer;
  private scene: StageScene;
  private camera: Camera | null = null;
  private elapsed = 0;

  constructor(worldContainer: Container) {
    this.worldContainer = worldContainer;

    // Create 3 parallax layers with different scroll rates
    this.skyLayer = new ParallaxLayer(0.05);
    this.deepLayer = new ParallaxLayer(0.2);
    this.midLayer = new ParallaxLayer(0.45);

    // Set z-indices and add to world
    this.skyLayer.container.zIndex = Z.BG_SKY;
    this.deepLayer.container.zIndex = Z.BG_DEEP;
    this.midLayer.container.zIndex = Z.BG_MID;

    worldContainer.addChild(this.skyLayer.container);
    worldContainer.addChild(this.deepLayer.container);
    worldContainer.addChild(this.midLayer.container);

    // Build default scene
    this.scene = createScene(DEFAULT_STAGE_ID);
    this.scene.build(this.getLayers(), getActiveTheme(DEFAULT_STAGE_ID));
  }

  /** Wire camera reference for parallax math. Call after camera init. */
  setCamera(camera: Camera): void {
    this.camera = camera;
    const pos = camera.getPosition();
    this.skyLayer.setHome(pos[0], pos[1]);
    this.deepLayer.setHome(pos[0], pos[1]);
    this.midLayer.setHome(pos[0], pos[1]);
  }

  /** Switch to a new stage theme — destroys old scene, builds new one. */
  setTheme(stageId: string): void {
    this.scene.destroy();
    this.scene = createScene(stageId);
    this.scene.build(this.getLayers(), getActiveTheme(stageId));
  }

  /** Toggle visibility of all parallax layers. */
  setVisible(visible: boolean): void {
    this.skyLayer.container.visible = visible;
    this.deepLayer.container.visible = visible;
    this.midLayer.container.visible = visible;
  }

  /** Per-frame update — parallax offsets + scene animation. */
  update(dt: number, wind?: number): void {
    this.elapsed += dt;

    // Update parallax offsets from camera position
    if (this.camera) {
      const pos = this.camera.getPosition();
      this.skyLayer.updateParallax(pos[0], pos[1]);
      this.deepLayer.updateParallax(pos[0], pos[1]);
      this.midLayer.updateParallax(pos[0], pos[1]);
    }

    this.scene.update(dt, this.elapsed, wind);
  }

  /** Delegate explosion reactivity to active scene. */
  onExplosion(x: number, y: number, radius: number): void {
    this.scene.onExplosion(x, y, radius);
  }

  private getLayers(): ParallaxLayers {
    return {
      sky: this.skyLayer,
      deep: this.deepLayer,
      mid: this.midLayer,
    };
  }
}
